import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, CheckCircle, XCircle, Eye, EyeOff, Info } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface PasswordAnalysis {
  score: number;
  strength: 'Very Weak' | 'Weak' | 'Fair' | 'Good' | 'Strong' | 'Very Strong';
  color: string;
  feedback: string[];
  timeToCrack: string;
  entropy: number;
}

const PasswordStrengthChecker: React.FC = () => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [analysis, setAnalysis] = useState<PasswordAnalysis | null>(null);

  useSEO({
    title: 'Password Strength Checker - Test Password Security Online',
    description: 'Check your password strength and security. Get detailed analysis, feedback, and suggestions to improve password security. Free online tool.',
    keywords: 'password strength checker, password security test, password analyzer, check password strength, password safety',
    canonical: 'https://snap-tools.online/security/password-strength-checker'
  });

  const analyzePassword = (pwd: string): PasswordAnalysis => {
    if (!pwd) {
      return {
        score: 0,
        strength: 'Very Weak',
        color: 'gray',
        feedback: ['Enter a password to analyze'],
        timeToCrack: 'N/A',
        entropy: 0
      };
    }

    let score = 0;
    const feedback: string[] = [];
    
    // Length analysis
    if (pwd.length < 8) {
      feedback.push('Password should be at least 8 characters long');
    } else if (pwd.length < 12) {
      score += 1;
      feedback.push('Consider using a longer password (12+ characters)');
    } else if (pwd.length < 16) {
      score += 2;
    } else {
      score += 3;
      feedback.push('Excellent length! Longer passwords are much more secure');
    }

    // Character type analysis
    const hasLowercase = /[a-z]/.test(pwd);
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasNumbers = /[0-9]/.test(pwd);
    const hasSymbols = /[^a-zA-Z0-9]/.test(pwd);
    
    let charTypeCount = 0;
    if (hasLowercase) charTypeCount++;
    if (hasUppercase) charTypeCount++;
    if (hasNumbers) charTypeCount++;
    if (hasSymbols) charTypeCount++;
    
    score += charTypeCount;
    
    if (!hasLowercase) feedback.push('Add lowercase letters (a-z)');
    if (!hasUppercase) feedback.push('Add uppercase letters (A-Z)');
    if (!hasNumbers) feedback.push('Add numbers (0-9)');
    if (!hasSymbols) feedback.push('Add special characters (!@#$%^&*)');
    
    if (charTypeCount >= 3) {
      feedback.push('Good mix of character types');
    }

    // Pattern analysis
    const hasRepeatingChars = /(.)\1{2,}/.test(pwd);
    const hasSequentialChars = /(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|123|234|345|456|567|678|789|890)/i.test(pwd);
    const hasKeyboardPatterns = /(qwerty|asdf|zxcv|qazwsx|123456|password)/i.test(pwd);
    
    if (hasRepeatingChars) {
      score -= 1;
      feedback.push('Avoid repeating characters (aaa, 111)');
    }
    
    if (hasSequentialChars) {
      score -= 1;
      feedback.push('Avoid sequential characters (abc, 123)');
    }
    
    if (hasKeyboardPatterns) {
      score -= 2;
      feedback.push('Avoid common keyboard patterns (qwerty, 123456)');
    }

    // Common password checks
    const commonPasswords = ['password', '123456', 'password123', 'admin', 'welcome', 'letmein', 'monkey', 'dragon'];
    if (commonPasswords.some(common => pwd.toLowerCase().includes(common))) {
      score -= 3;
      feedback.push('Avoid common passwords and words');
    }

    // Dictionary word check (simplified)
    const commonWords = ['hello', 'world', 'love', 'money', 'happy', 'friend', 'family', 'computer', 'internet'];
    if (commonWords.some(word => pwd.toLowerCase().includes(word))) {
      score -= 1;
      feedback.push('Avoid using dictionary words');
    }

    // Calculate entropy (simplified)
    const charsetSize = (hasLowercase ? 26 : 0) + (hasUppercase ? 26 : 0) + (hasNumbers ? 10 : 0) + (hasSymbols ? 32 : 0);
    const entropy = Math.log2(Math.pow(charsetSize, pwd.length));

    // Time to crack estimation (simplified)
    const guessesPerSecond = 1000000000; // 1 billion guesses per second (modern GPU)
    const totalCombinations = Math.pow(charsetSize, pwd.length);
    const secondsToCrack = totalCombinations / (2 * guessesPerSecond);
    
    let timeToCrack = '';
    if (secondsToCrack < 1) {
      timeToCrack = 'Instantly';
    } else if (secondsToCrack < 60) {
      timeToCrack = `${Math.round(secondsToCrack)} seconds`;
    } else if (secondsToCrack < 3600) {
      timeToCrack = `${Math.round(secondsToCrack / 60)} minutes`;
    } else if (secondsToCrack < 86400) {
      timeToCrack = `${Math.round(secondsToCrack / 3600)} hours`;
    } else if (secondsToCrack < 31536000) {
      timeToCrack = `${Math.round(secondsToCrack / 86400)} days`;
    } else if (secondsToCrack < 31536000000) {
      timeToCrack = `${Math.round(secondsToCrack / 31536000)} years`;
    } else {
      timeToCrack = 'Millions of years';
    }

    // Determine strength
    let strength: PasswordAnalysis['strength'] = 'Very Weak';
    let color = 'red';
    
    if (score <= 1) {
      strength = 'Very Weak';
      color = 'red';
    } else if (score <= 3) {
      strength = 'Weak';
      color = 'orange';
    } else if (score <= 5) {
      strength = 'Fair';
      color = 'yellow';
    } else if (score <= 7) {
      strength = 'Good';
      color = 'blue';
    } else if (score <= 9) {
      strength = 'Strong';
      color = 'green';
    } else {
      strength = 'Very Strong';
      color = 'emerald';
    }

    if (feedback.length === 0 || (feedback.length === 1 && feedback[0].includes('Excellent'))) {
      feedback.push('This is a strong password!');
    }

    return {
      score: Math.max(0, Math.min(10, score)),
      strength,
      color,
      feedback,
      timeToCrack,
      entropy: Math.round(entropy)
    };
  };

  useEffect(() => {
    const analysis = analyzePassword(password);
    setAnalysis(analysis);
  }, [password]);

  const getStrengthIcon = (strength: string) => {
    switch (strength) {
      case 'Very Weak':
      case 'Weak':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'Fair':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'Good':
      case 'Strong':
      case 'Very Strong':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <Shield className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Password Strength Checker</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Analyze your password security and get detailed feedback to improve its strength. Your password is never sent to our servers.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Password Input */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <label className="text-lg font-semibold text-gray-900">
                Enter Password to Analyze
              </label>
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Type your password here..."
              className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Analysis Results */}
          {analysis && password && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6 mb-6"
            >
              {/* Strength Meter */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getStrengthIcon(analysis.strength)}
                    <h3 className="text-lg font-semibold text-gray-900">Password Strength</h3>
                  </div>
                  <span className={`text-lg font-bold text-${analysis.color}-600`}>
                    {analysis.strength}
                  </span>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Strength Score: {analysis.score}/10</span>
                    <span>{Math.round((analysis.score / 10) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full bg-${analysis.color}-500 transition-all duration-500 ease-in-out`}
                      style={{ width: `${(analysis.score / 10) * 100}%` }}
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Entropy: </span>
                    <span className="font-semibold">{analysis.entropy} bits</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Time to crack: </span>
                    <span className="font-semibold">{analysis.timeToCrack}</span>
                  </div>
                </div>
              </div>

              {/* Feedback */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommendations</h3>
                <ul className="space-y-2">
                  {analysis.feedback.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Character Analysis */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Character Analysis</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                      /[a-z]/.test(password) ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <span className="text-sm font-semibold">a-z</span>
                    </div>
                    <p className="text-xs text-gray-600">Lowercase</p>
                  </div>
                  
                  <div className="text-center">
                    <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                      /[A-Z]/.test(password) ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <span className="text-sm font-semibold">A-Z</span>
                    </div>
                    <p className="text-xs text-gray-600">Uppercase</p>
                  </div>
                  
                  <div className="text-center">
                    <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                      /[0-9]/.test(password) ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <span className="text-sm font-semibold">0-9</span>
                    </div>
                    <p className="text-xs text-gray-600">Numbers</p>
                  </div>
                  
                  <div className="text-center">
                    <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                      /[^a-zA-Z0-9]/.test(password) ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <span className="text-sm font-semibold">!@#</span>
                    </div>
                    <p className="text-xs text-gray-600">Symbols</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Privacy Notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-green-800 mb-2">Privacy & Security</h4>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Your password is analyzed locally in your browser</li>
              <li>• No passwords are sent to our servers or stored anywhere</li>
              <li>• All analysis happens on your device for maximum security</li>
              <li>• Your data never leaves your computer</li>
            </ul>
          </div>

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">What Makes a Strong Password?</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Length:</strong> At least 12-16 characters</li>
                <li>• <strong>Complexity:</strong> Mix of uppercase, lowercase, numbers, symbols</li>
                <li>• <strong>Uniqueness:</strong> Different for each account</li>
                <li>• <strong>Unpredictability:</strong> Avoid personal information and common patterns</li>
                <li>• <strong>Memorability:</strong> Use passphrases or password managers</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">Common Attacks</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• <strong>Brute Force:</strong> Trying all possible combinations</li>
                <li>• <strong>Dictionary Attack:</strong> Using common words and passwords</li>
                <li>• <strong>Rainbow Tables:</strong> Pre-computed hash lookups</li>
                <li>• <strong>Social Engineering:</strong> Guessing based on personal info</li>
                <li>• <strong>Credential Stuffing:</strong> Reusing leaked passwords</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PasswordStrengthChecker;
